import React, { useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import moment from "moment";
import { Box, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterBuilder from "../../Shared/FilterBuilder/FilterBuilder";
import { filterListByMongoQuery } from "../../Shared/FilterBuilder/FilterBuilder.clientFilter";
import { ChangeSnackbar } from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import "./FreezeMasjidTable.scss";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress } from "@material-ui/core";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import { CellDataType, Masjid } from "../../../../Types";
import { GET_FREEZED_MASJIDS } from "../../../graphql/queries";
import { useMutation, useQuery } from "@apollo/client";
import { FREEZE_MASJID } from "../../../graphql/mutations";
import toast from "react-hot-toast";
import ReasonConfirmation from "../../Shared/ReasonConfirmation/ReasonConfirmation";
import { setQuarter } from "date-fns";
import freezeMasjidIcon from "../../../Photos/Newuiphotos/MasjidTable/FreezeMasjid.png";
import { Colors } from "../../../constants/Colors/colors";
import { useUpdateMasjidStatus } from "../../../graphql/Masjids/mutation";
import MasjidStatusHandler from "../../PageComponents/MasjidTable/MasjidStatusHandler";
import {
  getFormattedMasjidStatus,
  getStatus,
} from "../../PageComponents/MasjidTable/Helper";
import { getAssignedUserRoleLabel } from "../../../helpers/RoleDisplayHelper";
import { isDelegatedSubAdmin } from "../../../helpers/rbacExternalAdmin";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMasjidLedgers } from "../../../graphql/Masjids/query";
const today = moment();

type FreezeMasjidToolbarProps = {
  onOpenAdvancedFilter: () => void;
  hasActiveFilter: boolean;
};

const FreezeMasjidToolbar: React.FC<FreezeMasjidToolbarProps> = ({
  onOpenAdvancedFilter,
  hasActiveFilter,
}) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter debounceMs={500} />
    <Tooltip title="Advanced Filter">
      <IconButton
        onClick={onOpenAdvancedFilter}
        color={hasActiveFilter ? "primary" : "default"}
        data-testid="FilterListIcon"
        size="small"
        sx={{ ml: 1 }}
        aria-label="Advanced Filter"
      >
        <FilterListIcon />
        <Typography variant="body2" sx={{ ml: 0.5, display: { xs: "none", sm: "inline-flex" } }}>
          Advanced Filter
        </Typography>
      </IconButton>
    </Tooltip>
  </GridToolbarContainer>
);

const FreezeMasjidTable = () => {
  const dispatch = useAppThunkDispatch();
  const [FreezedMasjids, setFreezedMasjids] = useState<Masjid[]>([]);
  const [isUnFreezing, setisUnFreezing] = useState(false);
  const [FreezeModalOpen, setFreezeModalOpen] = useState(false);
  const [UnfreezingMasjid, setUnfreezingMasjid] = useState<Masjid | undefined>(
    undefined
  );
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState<string | null>(null);
  const { loading, error, data, refetch } = useQuery(GET_FREEZED_MASJIDS);
  const [selectedMasjidId, setSelectedMasjidId] = useState("");
  const [selectedMasjid, setSelectedMasjid] = useState<Masjid | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [masjidLogs, setMasjidLogs] = useState<any[]>([]);

  const {
    loading: loadingLedgers,
    error: ledgerError,
    data: logs,
    refetch: refetchLogs,
  } = useMasjidLedgers(
    selectedMasjid?._id ?? "",
    !selectedMasjid?._id || !showLogs
  );
  const {
    updateStatus,
    loading: statusLoading,
    error: statusError,
    data: statusData,
  } = useUpdateMasjidStatus();

  useEffect(() => {
    if (!loading && error) {
      toast.error("Error Fetching Freezed Masjids");
    }
  }, [loading, error, data]);
  const handleSubmit = async (status: string, reason: string) => {
    if (!reason || !status || !UnfreezingMasjid?._id) return;
    const freeze = status === "approved" ? false : true;
    try {
      await updateStatus({
        masjidId: UnfreezingMasjid?._id ?? "",
        description: reason,
        status,
        freeze,
      });
      toast.success("Successfully Updated Masjid Status");
      setReason("");
      setReasonError(false);
      refetch();
      setFreezeModalOpen(false);
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("Error Updating Masjid Status");
    }
  };

  const handleActionClick = (action: string, details: Masjid) => {
    setModalOpen(true);
    setSelectedMasjidId(details._id);
    setSelectedMasjid(details);
  };
  const handleModelOpen = (adminDetails: any) => {
    setFreezeModalOpen(true);
    setUnfreezingMasjid(adminDetails.row);
  };
  const userColumns: GridColDef[] = [
    {
      field: "masjidName",
      headerName: "Masjid Name",
      //autoComplete: "off",
      width: 280,
    },
    {
      field: "contact",
      headerName: "Contact",
      //autoComplete: "off",
      width: 170,
    },
    {
      field: "Days Before",
      headerName: "Days since last update",
      //autoComplete: "off",
      width: 100,
      hide: true,

      renderCell: (params: any) => {
        return (
          <div>
            {moment(today.format()).diff(moment(params.row.updatedAt), "days")}
          </div>
        );
      },
    },
    {
      field: "email",
      headerName: "email",
      width: 200,
      filterable: true,
      hide: true,
      renderCell: (params: any) => {
        for (const Link of params.row.externalLinks) {
          if (Link.name !== "Email") continue;
          else if (Link.name === "Email") {
            return <div>{Link.url}</div>;
          }
        }
      },
    },
    {
      field: "lastEditor",
      headerName: "Last Updated By",
      width: 200,
      editable: false,
      renderCell: (params: any) => {
        return <div>{params.row?.updatedBy?.name ?? ""}</div>;
      },
    },

    {
      field: "address",
      headerName: "Address",
      width: 240,
      //autoComplete: "off",
    },
    {
      field: "assignedUser[0].role",
      headerName: "Role",
      sortable: false,
      width: 110,
      valueGetter: (params: any) =>
        params.row.assignedUser?.length > 0
          ? getAssignedUserRoleLabel(params.row.assignedUser[0])
          : "",
      renderCell: (params: any) => {
        let color;
        if (params.row.assignedUser?.length > 0) {
          const assignedUser = params.row.assignedUser[0];
          if (
            assignedUser.role === "superadmin" ||
            assignedUser.role === "admin"
          ) {
            color = "blue"; // Set the color for superadmin/admin
          } else if (
            assignedUser.role === "subadmin" ||
            assignedUser.role === "musaliadmin" ||
            isDelegatedSubAdmin(assignedUser)
          ) {
            color = "green"; // Set the color for subadmin/musalliadmin
          } else {
            color = "black"; // Default color if the role is not recognized
          }
          const displayRole = getAssignedUserRoleLabel(assignedUser);
          return (
            <Typography style={{ color }} variant="body2">
              {displayRole}
            </Typography>
          );
        } else {
          return "";
        }
        // Check the role and set the color accordingly
      },
    },
    {
      field: "Freeze Reason",
      headerName: "Freeze Reason",
      width: 110,
      renderCell: (params: any) => {
        return (
          <div
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
            onClick={() => {
              setSelectedMasjid(params.row);
              setShowLogs(true); // open logs pop-up
            }}
          >
            <VisibilityIcon fontSize={"small"} />
          </div>
        );
      },
    },
    {
      field: "isVerified",
      headerName: "Verification Status",
      width: 110,
      valueGetter: (params: any) => {
        const value = getFormattedMasjidStatus(
          getStatus(params.row?.isFreezed, params.row?.isVerified)
        );
        return value;
      },
      renderCell: (params: any) => {
        let color;
        const value = getStatus(params.row?.isFreezed, params.row?.isVerified);
        // Check the role and set the color accordingly
        if (value === "approved" || value === null) {
          color = "#1B8368";
        } else if (value === "pending") {
          color = "#EDA920";
        } else {
          color = "#FF4949";
        }
        return (
          <Typography
            style={{ color, cursor: "pointer", textTransform: "capitalize" }}
            variant="body2"
            onClick={() => {
              handleActionClick(value, params.row);
            }}
          >
            {getFormattedMasjidStatus(
              getStatus(params.row?.isFreezed, params.row?.isVerified)
            )}
          </Typography>
        );
      },
    },
    {
      field: "assignedUser[0].name",
      headerName: "Admin Name",
      width: 150,
      sortable: false,
      valueGetter: (params: any) =>
        params.row.assignedUser?.length > 0
          ? params.row.assignedUser[0].name
          : "",
    },
    {
      field: "updatedAt",
      headerName: "Last Updated On",
      //autoComplete: "off",
      width: 220,
      renderCell: (params: any) => {
        return (
          <div className="dataUpdatedAt">
            {moment
              .utc(params.row.updatedAt)
              .local()
              .format("DD-MMM-YYYY HH:mm:ss")}
          </div>
        );
      },
    },
    {
      field: "followers",
      headerName: "Followers",
      width: 150,
      hide: true,
      valueGetter: (params: any) => params.row.followers ?? 0,
      // moment().format("MM/DD/YYYY hh:mm a"),
    },
    {
      field: "subscribers",
      headerName: "Musali",
      width: 150,
      hide: true,

      valueGetter: (params: any) => params.row.subscribers ?? 0,
      // valueGetter: (params: CellDataType) =>
      //   params.row.updatedAt,
      // moment().format("MM/DD/YYYY hh:mm a"),
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleModelOpen(params);
            }}
            style={{
              border: "1px solid green",
              color: "green",
              height: "20px",
              fontSize: "10px",
              cursor: "Pointer",
            }}
          >
            UnFreeze
          </Button>
        );
      },
    },

    {
      field: "createdAt",
      headerName: "Created At",
      width: 150,
      // flex: 1,
      // hideable: true,
      hide: true,
      valueGetter: (params: any) => {
        const value = params.row.createdAt;
        return moment.utc(value).local().format("DD-MMM-YYYY HH:mm:ss");
      },
    },
  ];

  const rawRows = !loading && data?.getFreezedMasjids?.length > 0 ? data.getFreezedMasjids : [];
  const displayRows = useMemo(
    () => (advancedFilter ? filterListByMongoQuery(rawRows, advancedFilter) : rawRows),
    [rawRows, advancedFilter]
  );

  useEffect(() => {
    if (!loadingLedgers && logs) {
      setMasjidLogs(logs);
    }
  }, [loadingLedgers, logs]);

  const handleCloseUnFreezeModal = () => {
    setReasonError(false);
    setReason("");
    setFreezeModalOpen(false);
  };

  return (
    <div className="FreezedMasjidTableContainer">
      {showLogs && (
        <Dialog open onClose={() => setShowLogs(false)}>
          <DialogTitle>Reason for freezing</DialogTitle>
          <DialogContent>
            {loadingLedgers
              ? "Loading..."
              : masjidLogs[0]?.description ?? "No reason recorded."}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLogs(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        onClose={handleCloseUnFreezeModal}
        open={FreezeModalOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "22px",
            padding: "12px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            textAlign: "center",
            color: Colors.greenBlack,
            fontSize: "16px",
            fontWeight: "600",
            padding: "10px",
          }}
        >
          {/* {`Are you sure you want to Unfreeze the Masjid ?`} */}
          {/* {showFinalConfirmation ? (
            <img
              src={freezeMasjidIcon}
              alt="Freeze Masjid"
              style={{
                width: "45px",
              }}
            />
          ) : ( */}
          Reason To Unfreeze
          {/* )} */}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflowX: "hidden",
            minHeight: "400px",
          }}
        >
          {/* {showFinalConfirmation ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Are You Sure You Want to UnFreeze this masjid?
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  width: "40%",
                }}
                mt={2}
              >
                <Button
                  sx={{
                    bgcolor: "#F93737",
                    "&:hover": {
                      bgcolor: "#F93737", // Changes background to a slightly darker red on hover
                    },
                    color: "#fff",
                    borderRadius: " 12px",
                    padding: "6px 25px",
                  }}
                  onClick={() => {
                    setShowFinalConfirmation(false);
                    setFreezeModalOpen(true);
                  }}
                >
                  No
                </Button>
                <Button
                  sx={{
                    bgcolor: "#1B8368",
                    color: "#fff",
                    borderRadius: " 12px",
                    padding: "6px 25px",
                    "&:hover": {
                      bgcolor: "#1B8368", // Changes background to a slightly darker red on hover
                    },
                  }}
                >
                  Yes
                </Button>
              </Box>
            </Box>
          ) : ( */}
          <ReasonConfirmation
            onConfirm={(status) => {
              if (!reason) {
                setReasonError(true);
                return;
              }
              handleSubmit(status, reason);
              // setFreezeModalOpen(false);
              // setShowFinalConfirmation(true);

              // console.log("status change to ", status);
            }}
            newStatus={"approved"}
            reason={reason}
            setReason={setReason}
            reasonError={reasonError}
            setReasonError={setReasonError}
            isLoading={statusLoading}
          />
          {/* )} */}
        </DialogContent>
        {/* <DialogActions style={{ justifyContent: "space-around" }}>
          <Button
            onClick={handleUnFreezeModal}
            variant="outlined"
            disabled={fLoading}
          >
            No
          </Button>
          <Button
            onClick={() => handleFreezeMasjid(UnfreezingMasjid?._id)}
            variant="outlined"
          >
            {" "}
            {fLoading ? (
              <CircularProgress
                //  color="black"
                size="15px"
              />
            ) : (
              <span> Yes </span>
            )}{" "}
          </Button>
        </DialogActions> */}
      </Dialog>
      <div
        style={{
          height: 700,
          width: "100%",
          marginTop: "1.2rem",
          backgroundColor: "white",
          borderRadius: "20px",
        }}
      >
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              userSelect: "text",
            },
          }}
          loading={loading}
          rows={displayRows}
          columns={userColumns}
          pageSize={10}
          getRowId={(row: Masjid) => row._id}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          components={{ Toolbar: FreezeMasjidToolbar }}
          componentsProps={{
            toolbar: {
              onOpenAdvancedFilter: () => setFilterBuilderOpen(true),
              hasActiveFilter: !!advancedFilter,
            },
          }}
        />
      </div>
      <MasjidStatusHandler
        refetch={refetch}
        open={modalOpen}
        setOpen={setModalOpen}
        onConfirm={() => {}}
        selectedMasjid={selectedMasjid as Masjid}
        setSelectedMasjid={setSelectedMasjid}
      />
      <FilterBuilder
        entityType="MASJID"
        open={filterBuilderOpen}
        onClose={() => setFilterBuilderOpen(false)}
        onApply={(filter) => setAdvancedFilter(filter)}
        pageId="freezed-masjids"
      />
    </div>
  );
};

export default FreezeMasjidTable;
